import {
  programmeQuery,
  programmeTasksQuery,
  useRemoveProgrammeTaskMutation,
} from "@common/api/maintenance-programme"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { handleError } from "@common/utils"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { revisionLabel } from "@/routes/_layout/maintenance-programmes/-lib/revision-label"
import { useViewedRevision } from "../../-lib/revision"
import { TASKS } from "../../-lib/search"

export const Route = createFileRoute(
  "/_layout/maintenance-programmes_/$programmeId/tasks/remove/$taskId",
)({
  component: RemoveTaskDialog,
})

function RemoveTaskDialog(): React.ReactElement {
  const { programmeId, taskId } = Route.useParams()
  const { data: programme } = useSuspenseQuery(
    programmeQuery({ programme_id: programmeId }),
  )
  const revision = useViewedRevision(programme)
  const { data: tasks } = useQuery({
    ...programmeTasksQuery({
      programme_id: programmeId,
      revision_id: revision?.id ?? "",
      ...TASKS,
    }),
    enabled: revision !== undefined,
  })
  const mutation = useRemoveProgrammeTaskMutation()
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const showToast = useCustomToast()
  const task = tasks?.data.find(({ id }) => id === taskId)
  const label = task?.task_number ?? task?.title ?? "this task"
  const toList = () =>
    navigate({
      to: "/maintenance-programmes/$programmeId",
      params: { programmeId },
      search: true,
    })
  const back = () => (canGoBack ? router.history.back() : toList())

  const confirm = () =>
    revision &&
    mutation.mutate(
      { programme_id: programmeId, revision_id: revision.id, task_id: taskId },
      {
        onSuccess: () => {
          showToast(
            `Removed ${label}`,
            revisionLabel(revision.number),
            "success",
          )
          toList()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title={`Remove ${label} from ${revision ? revisionLabel(revision.number) : "the revision"}?`}
      description="The task stays in earlier revisions. Adding it back later creates a new task."
      action="Remove"
      pendingAction="Removing…"
      pending={mutation.isPending}
      disabled={!task}
      onConfirm={confirm}
      onCancel={back}
    />
  )
}
